import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "@/components/Reveal";

const chapters = [
  {
    n: "01",
    title: "We Bring the Bar",
    body: "No venue? No problem. Our fully-stocked mobile bar arrives with everything — glassware, ice, garnishes, and a master bartender ready to pour.",
  },
  {
    n: "02",
    title: "Menus, Made Personal",
    body: "From your signature wedding cocktail to a smoked old fashioned flight, every menu is designed around your palate and your story.",
  },
  {
    n: "03",
    title: "Theatre in Every Pour",
    body: "Precision, flair, and hospitality. Our team turns service into spectacle so your guests remember the drink and the moment.",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="manifesto"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 max-w-7xl mx-auto"
      data-testid="manifesto-section"
    >
      <Reveal className="mb-16 md:mb-24 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">The Philosophy</p>
        <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
          Not a caterer.
          <br />
          <span className="italic text-[#a0a0a0]">A movable</span>{" "}
          <span className="gold-shimmer italic">institution.</span>
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7 flex flex-col">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="grid grid-cols-12 gap-6 py-10 border-t border-[#222] group">
                <div className="col-span-3 md:col-span-2">
                  <span className="font-heading text-5xl md:text-6xl text-gold/80 group-hover:text-gold transition-colors">
                    {c.n}
                  </span>
                </div>
                <div className="col-span-9 md:col-span-10">
                  <h3 className="font-heading text-2xl md:text-3xl mb-3">{c.title}</h3>
                  <p className="text-[#a0a0a0] font-light leading-relaxed max-w-lg">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="lg:col-span-5 relative h-[420px] md:h-[600px] overflow-hidden">
          <motion.img
            style={{ y: imgY }}
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_smooth-spirits/artifacts/vyax31a7_IMG_2624.webp"
            alt="Bartending Iz Life master bartender with cocktail shaker"
            className="absolute inset-0 w-full h-[125%] object-cover object-top contrast-105"
            data-testid="manifesto-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 border-t border-gold/40 pt-4">
            <p className="font-heading italic text-xl text-white/90">"Every gathering deserves a great bar."</p>
          </div>
        </div>
      </div>
    </section>
  );
}
